import { useQuery } from "@tanstack/react-query";
import { getCourses } from "../api/Courses/GetAllCourses";
import CourseCard from "../components/Home/CourseCard";
import Spinner from "../components/Shared/Spinner";
import { Helmet } from "react-helmet";
import { SetStateAction, useState, useEffect } from "react";
import { GetPackages } from "../api/Packages/GetAllPackages";
import PackageCard from "../components/Package/PackageCard";
import CoursesHeader from "../components/Course/CoursesHeader";
import { getCoursesOffline } from "../api/OfflineCourses/GetAllOfflineCourses";
import CoursesFiltering from "../components/Course/CoursesFiltering";
import CoursePagination from "../components/Course/CoursePagination";
import { CoursesDTO } from "../api/Courses/GetAllCourses";
import { GetAllCategories } from "../api/Categories/GetAllCategories";
import { useSearchParams } from "react-router-dom";

const Courses = () => {
  const [activeTab, setActiveTab] = useState("courses");
  const [sortedCourses, setSortedCourses] = useState<CoursesDTO[]>([]);
  const [filteredCourses, setFilteredCourses] = useState<CoursesDTO[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const coursesPerPage = 6;
  const [searchParams] = useSearchParams();

  const { data: CoursesResponse, isLoading: isCoursesLoading } = useQuery({
    queryFn: getCourses,
    queryKey: ["courses"],
    staleTime: 0,
    refetchInterval: 0,
  });
  const { data: PackagesResponse, isLoading: isPackageLoading } = useQuery({
    queryFn: GetPackages,
    queryKey: ["packages"],
    staleTime: 0,
    refetchInterval: 0,
  });

  const { data: CoursesResponseOffline, isLoading: isCoursesOfflineLoading } =
    useQuery({
      queryFn: getCoursesOffline,
      queryKey: ["coursesOffline"],
      staleTime: 0,
      refetchInterval: 0,
    });
  const offlineCourses = CoursesResponseOffline?.data?.coursesData || [];

  const { data: CategoriesDTO } = useQuery({
    queryFn: GetAllCategories,
    queryKey: ["categories"],
    staleTime: 0,
    refetchInterval: 0,
  });
  const packages = PackagesResponse?.data?.pacakgeData || [];

  const courses = CoursesResponse?.data?.coursesData || [];

  const allCourses = [...(courses || []), ...(offlineCourses || [])];

  const indexOfLastCourse = currentPage * coursesPerPage;
  const indexOfFirstCourse = indexOfLastCourse - coursesPerPage;
  const currentCourses = filteredCourses.slice(
    indexOfFirstCourse,
    indexOfLastCourse
  );
  const totalPages = Math.ceil(filteredCourses.length / coursesPerPage);

  const firstItemIndex = indexOfFirstCourse + 1;
  const lastItemIndex = Math.min(indexOfLastCourse, filteredCourses.length);
  const totalItems = filteredCourses.length;

  useEffect(() => {
    const categoryParam = searchParams.get("category");

    if (allCourses.length > 0 && CategoriesDTO?.data) {
      setSortedCourses(allCourses);

      if (categoryParam) {
        const categoryId = CategoriesDTO.data.find(
          (cat) =>
            cat.name.toLowerCase() ===
            decodeURIComponent(categoryParam).toLowerCase()
        )?.id;

        if (categoryId) {
          const filtered = allCourses.filter(
            (course) => course.categoryId === categoryId
          );
          setFilteredCourses(filtered);
        } else {
          setFilteredCourses(allCourses);
        }
      } else {
        setFilteredCourses(allCourses);
      }
    }
  }, [allCourses, CategoriesDTO?.data, searchParams]);

  const handleSort = (
    sortType: "alphabetical" | "date" | "price-high" | "price-low" | null
  ) => {
    let sorted;
    if (sortType === null) {
      sorted = [...allCourses];
    } else if (sortType === "alphabetical") {
      sorted = [...allCourses].sort((a, b) => a.name.localeCompare(b.name));
    } else if (sortType === "date") {
      sorted = [...allCourses].sort(
        (a, b) =>
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      );
    } else if (sortType === "price-high") {
      sorted = [...allCourses].sort((a, b) => {
        const priceA = a.discountPrice_egp || a.actualPrice_egp;
        const priceB = b.discountPrice_egp || b.actualPrice_egp;
        return Number(priceB) - Number(priceA);
      });
    } else if (sortType === "price-low") {
      sorted = [...allCourses].sort((a, b) => {
        const priceA = a.discountPrice_egp || a.actualPrice_egp;
        const priceB = b.discountPrice_egp || b.actualPrice_egp;
        return Number(priceA) - Number(priceB);
      });
    }
    setSortedCourses(sorted ?? []);
    setFilteredCourses(sorted ?? []);
  };

  const handleSearch = (filters: {
    courseName: string;
    instructorName: string;
    city: string;
    categories: string[];
  }) => {
    const filtered = sortedCourses.filter((course) => {
      const matchesCourseName = course.name
        .toLowerCase()
        .includes(filters.courseName.toLowerCase());
      const matchesInstructor = (
        course.Instructor.firstName +
        " " +
        course.Instructor.lastName
      )
        .toLowerCase()
        .includes(filters.instructorName.toLowerCase());
      const matchesCity = course.city
        ?.toLowerCase()
        .includes(filters.city.toLowerCase());
      const matchesCategory =
        filters.categories.length === 0 ||
        filters.categories.includes(course.categoryId);

      return (
        (!filters.courseName || matchesCourseName) &&
        (!filters.instructorName || matchesInstructor) &&
        (!filters.city || matchesCity) &&
        matchesCategory
      );
    });

    setFilteredCourses(filtered);
  };

  const handleTabSwitch = (tab: SetStateAction<string>) => {
    setActiveTab(tab);
  };

  const handlePageChange = (pageNumber: number) => {
    setCurrentPage(pageNumber);
    // Scroll to top of courses section
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleClearAll = () => {
    setSortedCourses(allCourses ?? []);
    setFilteredCourses(allCourses ?? []);
    setCurrentPage(1);
  };

  return (
    <>
      <Helmet>
        <link rel="canonical" href="https://www.nilewisect.com/courses" />
        <meta name="robots" content="index, follow" />
        <meta
          name="description"
          content="Prepare for the CMA test with our comprehensive CMA course. Gain the knowledge and skills needed to excel and become a Certified Management Accountant."
        />
        <meta
          name="title"
          content="CMA Test Preparation: Comprehensive CMA Course Online"
        />
      </Helmet>

      <CoursesHeader />

      <div className="pb-32 pt-20 container">
        <div className="flex md:mb-14 mb-8 pb-0.5 items-center justify-center sm:gap-10 gap-2 bg-gray-100 rounded-full w-fit mx-auto sm:px-10 px-5 sm:h-20  h-[70px]">
          <div
            className={`flex transition-all items-center justify-center duration-300 font-bold  sm:text- text-burntBlack  w-full sm:w-auto cursor-pointer gap-2 ${
              activeTab === "courses"
                ? "bg-secondary/20 rounded-full px-4 py-2 text-secondary"
                : ""
            } `}
            onClick={() => handleTabSwitch("courses")}
          >
            <p>Courses</p>{" "}
            <div
              className={` sm:text-base flex items-center justify-center text-sm size-6 text-center rounded-full   ${
                activeTab === "courses"
                  ? "text-white bg-secondary"
                  : "bg-primary/30"
              }  `}
            >
              {allCourses.length}
            </div>
          </div>
          <div
            className={`flex transition-all items-center justify-center duration-300 font-bold  sm:text- text-burntBlack  w-full sm:w-auto cursor-pointer gap-2 ${
              activeTab === "packages"
                ? "bg-secondary/20 rounded-full px-4 py-2 text-secondary"
                : ""
            } `}
            onClick={() => handleTabSwitch("packages")}
          >
            <p>Packages</p>{" "}
            <div
              className={` sm:text-base flex items-center justify-center text-sm size-6 text-center rounded-full   ${
                activeTab === "packages"
                  ? "text-white bg-secondary"
                  : "bg-primary/30"
              }  `}
            >
              {packages.length}
            </div>
          </div>
        </div>

        {activeTab === "courses" &&
          (isCoursesLoading || isCoursesOfflineLoading ? (
            <div className="min-h-[50vh] flex items-center justify-center">
              <Spinner />
            </div>
          ) : (
            <>
              <CoursesFiltering
                onSort={handleSort}
                onSearch={handleSearch}
                onClearAll={handleClearAll}
                categories={CategoriesDTO?.data || []}
              />
              {filteredCourses.length > 0 ? (
                <>
                  <div className="grid lg:grid-cols-3 sm:grid-cols-2 grid-cols-1 md:gap-x-8 gap-x-4 gap-y-5 sm:gap-y-8 md:gap-y-12">
                    {currentCourses.map((course) => (
                      <CourseCard key={course.id} course={course} />
                    ))}
                  </div>
                  {totalPages > 1 && (
                    <CoursePagination
                      currentPage={currentPage}
                      totalPages={totalPages}
                      onPageChange={handlePageChange}
                    />
                  )}
                  <div className="text-center text-gray-600 mt-4 sm:text-base text-sm">
                    Showing {firstItemIndex} – {lastItemIndex}{" "}
                    {lastItemIndex === 1 ? "Course" : "Courses"} of {totalItems}{" "}
                    {totalItems === 1 ? "Course" : "Courses"}
                  </div>
                </>
              ) : (
                <div className="text-center my-10">
                  <h2 className="text-lg text-burntBlack">
                    No courses match your search criteria.
                  </h2>
                </div>
              )}
            </>
          ))}
        {activeTab === "packages" &&
          (isPackageLoading ? (
            <div className="min-h-[50vh] flex items-center justify-center">
              <Spinner />
            </div>
          ) : (
            <>
              {packages.length > 0 ? (
                <div className="grid lg:grid-cols-3 md:grid-cols-2 grid-cols-1 md:gap-x-8 gap-x-4 gap-y-5 sm:gap-y-8 md:gap-y-12">
                  {packages.map((pack) => (
                    <PackageCard key={pack.id} packages={pack} />
                  ))}
                </div>
              ) : (
                <div className="text-center my-10">
                  <h2 className="text-lg text-burntBlack">
                    No Packages available for this category.
                  </h2>
                </div>
              )}
            </>
          ))}
      </div>
    </>
  );
};

export default Courses;
